import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import Logo from '../components/Logo';
import {
  fetchRole,
  submitApplication,
  formatPay,
  EMPLOYMENT_LABELS,
  RoleDetail,
  ApplicationPayload,
} from '../services/careers';
import './Careers.css';

/**
 * One role: the full job description, then the application form.
 *
 * The form's questions come from the API (`screening_questions`), not from this
 * file — a role's intake is data, so adding a question is a seeder run, not a
 * deploy. The server re-checks every required answer; the client-side check
 * here is only there to save the applicant a round trip.
 */

const CareerRole: React.FC = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [role, setRole] = useState<RoleDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState<{ reference: string; message: string } | null>(null);

  // ?src=telegram&ref=<who sent them> — how we learn which channel actually works.
  const source = params.get('src') || 'careers_page';
  const referrer = params.get('ref') || undefined;

  useEffect(() => {
    fetchRole(slug)
      .then((r) => {
        setRole(r);
        document.title = `${r.title} — Jobs at Lomi`;
      })
      .catch((e) => setError(e.message));
  }, [slug]);

  const pay = useMemo(() => (role ? formatPay(role) : null), [role]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const setAnswer = (k: string, v: string) => setAnswers((a) => ({ ...a, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!role) return;
    setSubmitError(null);

    const missing = role.screening_questions.find(
      (q) => q.required && !(answers[q.key] || '').trim()
    );
    if (missing) {
      setSubmitError(`Please answer: ${missing.label}`);
      return;
    }

    const payload: ApplicationPayload = {
      full_name: form.full_name || '',
      phone: form.phone || '',
      email: form.email || undefined,
      telegram_username: form.telegram_username || undefined,
      city: form.city || undefined,
      current_position: form.current_position || undefined,
      years_experience: form.years_experience ? Number(form.years_experience) : undefined,
      portfolio_url: form.portfolio_url || undefined,
      cv_url: form.cv_url || undefined,
      answers,
      source,
      referrer,
    };

    setSubmitting(true);
    try {
      const res = await submitApplication(slug, payload);
      setDone({ reference: res.reference, message: res.message });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setSubmitError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="c-page">
        <div className="c-detail-empty">
          <h1>{error}</h1>
          <button className="c-btn" onClick={() => navigate('/careers')}>
            See the roles that are open →
          </button>
        </div>
      </div>
    );
  }

  if (!role) return <div className="c-page"><p className="c-loading">Loading…</p></div>;

  return (
    <div className="c-page">
      <header className="c-header">
        <button className="c-back" onClick={() => navigate('/careers')}>← All roles</button>
        <div className="c-brand" onClick={() => navigate('/')}><Logo height={26} withAmharic /></div>
      </header>

      {done ? (
        <section className="c-done">
          <div className="c-done-mark">🍋</div>
          <h1>Application received</h1>
          <p className="c-done-ref">
            Your reference is <strong>{done.reference}</strong> — keep it.
          </p>
          <p>{done.message}</p>
          <div className="c-done-next">
            <h3>What happens now</h3>
            <ol>
              <li>We read every application ourselves. You'll hear back within <strong>3 days</strong>, yes or no.</li>
              <li>If we go further, we send you a short piece of real work — and we pay you for it.</li>
              <li>A video call with the founder, then coffee in Addis.</li>
              <li>Final answer within 21 days of today. From a person, either way.</li>
            </ol>
          </div>
          <p className="c-done-gift">
            In the meantime, Lomi is yours free — for you and two family members. Sign up with
            this same phone number and it's already switched on.
          </p>
          <div className="c-done-actions">
            <button className="c-btn" onClick={() => navigate('/')}>Try Lomi →</button>
            <button className="c-btn c-btn-ghost" onClick={() => navigate('/careers')}>
              See other roles
            </button>
          </div>
        </section>
      ) : (
        <>
          <section className="c-detail-hero">
            <p className="c-eyebrow">{role.team_label}</p>
            <h1>{role.title}</h1>
            {role.summary && <p className="c-detail-sub">{role.summary}</p>}
            <div className="c-detail-facts">
              {pay && <div className="c-fact c-fact-pay"><span>Pay</span><strong>{pay}</strong></div>}
              <div className="c-fact">
                <span>Type</span>
                <strong>{EMPLOYMENT_LABELS[role.employment_type] || role.employment_type}</strong>
              </div>
              {role.location && <div className="c-fact"><span>Location</span><strong>{role.location}</strong></div>}
              <div className="c-fact">
                <span>Openings</span><strong>{role.openings}</strong>
              </div>
              {role.apply_by && (
                <div className="c-fact"><span>Apply by</span><strong>{role.apply_by}</strong></div>
              )}
            </div>
            {role.comp_note && <p className="c-detail-compnote">{role.comp_note}</p>}
            <a href="#apply" className="c-btn c-btn-lg">Apply for this role →</a>
          </section>

          <article className="c-jd">
            <ReactMarkdown remarkPlugins={[gfm]}>{role.body_md}</ReactMarkdown>
          </article>

          <section className="c-apply" id="apply">
            <h2>Apply</h2>
            <p className="c-apply-intro">
              About ten minutes. No CV needed — we'd rather read your answers. Everything is
              read by a person, and you'll hear back within three days.
            </p>

            <form onSubmit={handleSubmit} className="c-form">
              <div className="c-field-row">
                <label className="c-field">
                  <span>Full name *</span>
                  <input required value={form.full_name || ''} onChange={set('full_name')} />
                </label>
                <label className="c-field">
                  <span>Phone *</span>
                  <input
                    required
                    inputMode="tel"
                    placeholder="09…"
                    value={form.phone || ''}
                    onChange={set('phone')}
                  />
                </label>
              </div>

              <div className="c-field-row">
                <label className="c-field">
                  <span>Telegram username</span>
                  <input placeholder="@yourname" value={form.telegram_username || ''} onChange={set('telegram_username')} />
                </label>
                <label className="c-field">
                  <span>City</span>
                  <input placeholder="Addis Ababa" value={form.city || ''} onChange={set('city')} />
                </label>
              </div>

              <div className="c-field-row">
                <label className="c-field">
                  <span>What do you do now?</span>
                  <input
                    placeholder="e.g. Physics teacher, Kotebe Secondary"
                    value={form.current_position || ''}
                    onChange={set('current_position')}
                  />
                </label>
                <label className="c-field">
                  <span>Email</span>
                  <input type="email" value={form.email || ''} onChange={set('email')} />
                </label>
              </div>

              {role.screening_questions.map((q) => (
                <label className="c-field c-field-full" key={q.key}>
                  <span>
                    {q.label} {q.required && '*'}
                  </span>
                  {q.type === 'textarea' ? (
                    <textarea
                      rows={4}
                      value={answers[q.key] || ''}
                      onChange={(e) => setAnswer(q.key, e.target.value)}
                    />
                  ) : q.type === 'select' ? (
                    <select value={answers[q.key] || ''} onChange={(e) => setAnswer(q.key, e.target.value)}>
                      <option value="">Choose…</option>
                      {(q.options || []).map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <input value={answers[q.key] || ''} onChange={(e) => setAnswer(q.key, e.target.value)} />
                  )}
                </label>
              ))}

              <label className="c-field c-field-full">
                <span>A link to your work, if you have one (portfolio, channel, writing)</span>
                <input placeholder="https://" value={form.portfolio_url || ''} onChange={set('portfolio_url')} />
              </label>

              <label className="c-field c-field-full">
                <span>CV link — optional, genuinely</span>
                <input placeholder="Google Drive or Dropbox link" value={form.cv_url || ''} onChange={set('cv_url')} />
              </label>

              {submitError && <p className="c-form-error">{submitError}</p>}

              <button className="c-btn c-btn-lg" type="submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send my application →'}
              </button>
              <p className="c-form-fine">
                We use what you send only to consider you for this role. We don't share it, and
                we don't sell it.
              </p>
            </form>
          </section>
        </>
      )}
    </div>
  );
};

export default CareerRole;
